import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { CONSENT } from '@/common/constants/common';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetStaffAllListQuery } from '@/api/common';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditStopServiceForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const userInfo = useSelector(selectMyInfo);

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
			dateOfTwcMembershipStart: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			dateOfTermination: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			signatureDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			serviceTargetReachedSpecify: Yup.string().when('serviceTargetReached', {
				is: (serviceTargetReached: string) => serviceTargetReached,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			serviceTerminationOthersSpecify: Yup.string().when(
				'serviceTerminationOthers',
				{
					is: (serviceTerminationOthers: string) => serviceTerminationOthers,
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchServiceTargetReachedSpecify = methods.watch(
			'serviceTargetReached'
		);
		const watchServiceTerminationOthers = methods.watch(
			'serviceTerminationOthers'
		);

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.serviceUserInformation')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameZh'
									label={t('preAdmission.serviceUserName') + '*'}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='dateOfTwcMembershipStart'
									label={t('preAdmission.dateOfTwcMembershipStart') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='dateOfTermination'
									label={t('preAdmission.zh_dateOfTermination') + '*'}
								/>
							</Grid>
							<Grid size={12}>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='isCase'
										label={t('preAdmission.zh_isCase')}
									/>
									<SingleCheckbox
										control={methods.control}
										name='isMember'
										label={t('preAdmission.zh_isMember')}
									/>
								</Box>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Typography
							variant='body1'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.reasonsForWithdrawal')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='serviceTargetReached'
										label={t('preAdmission.serviceTargetReached')}
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										handleChange={() => {
											methods.setValue('serviceTargetReachedSpecify', '');
											methods.clearErrors('serviceTargetReachedSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchServiceTargetReachedSpecify}
										variantType={'standard'}
										control={methods.control}
										name='serviceTargetReachedSpecify'
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t('preAdmission.serviceTargetReachedSpecify')}${
											watchServiceTargetReachedSpecify ? '*' : ''
										}`}
									/>
								</Box>
								<SingleCheckbox
									control={methods.control}
									name='admittedToResidential'
									label={t('preAdmission.zh_admittedToResidential')}
								/>
								<SingleCheckbox
									control={methods.control}
									name='admittedToPsychiatricHospital'
									label={t('preAdmission.zh_admittedToPsychiatricHospital')}
								/>
								<SingleCheckbox
									control={methods.control}
									name='persistentlyUnmotivated'
									label={t('preAdmission.zh_persistentlyUnmotivated')}
								/>
								<SingleCheckbox
									control={methods.control}
									name='selfWithdrawal'
									label={t('preAdmission.zh_selfWithdrawal')}
								/>
								<SingleCheckbox
									control={methods.control}
									name='untraceable'
									label={t('preAdmission.zh_untraceable')}
								/>

								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='serviceTerminationOthers'
										label={t('preAdmission.serviceTerminationOthers')}
										handleChange={() => {
											methods.setValue('serviceTerminationOthersSpecify', '');
											methods.clearErrors('serviceTerminationOthersSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchServiceTerminationOthers}
										variantType={'standard'}
										control={methods.control}
										name='serviceTerminationOthersSpecify'
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t(
											'preAdmission.serviceTerminationOthersSpecify'
										)}${watchServiceTerminationOthers ? '*' : ''}`}
									/>
								</Box>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									multiline
									name='remarks'
									label={t('preAdmission.zh_remarks')}
									rows={2}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameZh'
									label={t('preAdmission.serviceUserName') + '*'}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='workerId'
									label={t('preAdmission.workerName')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs()
										.startOf('day')
										.format(API_DATE_FORMAT)}
									control={methods.control}
									name='signatureDate'
									label={t('preAdmission.signatureDate')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditStopServiceForm.displayName = 'EditStopServiceForm';

export default EditStopServiceForm;
